import type { HpAgentLicenseType } from '@zg-rentals/ts-hotpads-api-client';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { sanitizeLegalDisclaimer } from '@/src/components/features/property/LegalInfoModule/legalInfoModule.utils';

type LicenseValueKey =
  | 'sourceLicenseAssociateRealEstateBroker'
  | 'sourceLicenseRealEstateBroker'
  | 'sourceLicenseRealEstateSalesPerson';

type SourceInfoRow = {
  labelKey: 'sourceContactLabel' | 'sourceLicenseLabel' | 'sourceCompanyLabel' | 'sourcePhoneLabel';
  value: string;
  /** Message key for a value that needs a translated label instead of the raw API string. */
  valueKey?: LicenseValueKey;
};

const LICENSE_VALUE_KEYS: Record<`${HpAgentLicenseType}`, LicenseValueKey> = {
  associateRealEstateBroker: 'sourceLicenseAssociateRealEstateBroker',
  realEstateBroker: 'sourceLicenseRealEstateBroker',
  realEstateSalesPerson: 'sourceLicenseRealEstateSalesPerson',
};

const licenseValueKey = (value: string): LicenseValueKey | undefined =>
  Object.hasOwn(LICENSE_VALUE_KEYS, value) ? LICENSE_VALUE_KEYS[value as `${HpAgentLicenseType}`] : undefined;

export type SourceInfoDisplay = {
  brokerRows: Array<SourceInfoRow>;
  hasBrokerInfo: boolean;
  hasMlsInfo: boolean;
  mlsLogo: string;
  mlsName: string;
  disclaimerParagraphs: Array<string>;
};

const cleanString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const firstCleanString = (...values: Array<unknown>): string => values.map(cleanString).find(Boolean) ?? '';

const getBrokerRows = (listing: PropertyDetailListing | null | undefined): Array<SourceInfoRow> => {
  const broker = listing?.broker;
  const shouldShowBroker =
    broker?.isBrokerExclusiveListing === true ||
    broker?.isBrokerListing === true ||
    listing?.details?.isBrokerListing === true;

  if (!shouldShowBroker) return [];

  const license = cleanString(broker?.agentLicenseType);
  const licenseKey = licenseValueKey(license);
  const rows: Array<SourceInfoRow> = [
    { labelKey: 'sourceContactLabel', value: cleanString(broker?.contactName) },
    {
      labelKey: 'sourceLicenseLabel',
      value: license,
      ...(licenseKey ? { valueKey: licenseKey } : {}),
    },
    { labelKey: 'sourceCompanyLabel', value: cleanString(broker?.companyName) },
    {
      labelKey: 'sourcePhoneLabel',
      value: cleanString(broker?.companyPhoneNumber),
    },
  ];

  return rows.filter((row) => row.value);
};

export const getSourceInfoDisplay = (listing: PropertyDetailListing | null | undefined): SourceInfoDisplay => {
  const details = listing?.details;
  const listedBy = listing?.listedBy;
  const brokerRows = getBrokerRows(listing);
  const mlsName = firstCleanString(details?.mlsName);
  const mlsLogo = firstCleanString(details?.mlsLogo, listedBy?.mlsLogo);
  const disclaimerParagraphs = sanitizeLegalDisclaimer(firstCleanString(details?.disclaimer, listedBy?.disclaimer));
  const hasMlsInfo = details?.isMls === true && Boolean(mlsName || mlsLogo || disclaimerParagraphs.length > 0);

  return {
    brokerRows,
    hasBrokerInfo: brokerRows.length > 0,
    hasMlsInfo,
    mlsLogo,
    mlsName,
    disclaimerParagraphs,
  };
};

export const hasSourceInfoData = (listing: PropertyDetailListing | null | undefined): boolean => {
  const display = getSourceInfoDisplay(listing);
  return display.hasBrokerInfo || display.hasMlsInfo;
};
